/**
 * "Bugün" ekranı sözleşmesi (v3 C1; Planports F-19, Teamgram "Beni bekleyenler" / "Sıradaki adımlar" karşılığı, kısmi).
 *
 * - Kovalar (sıra sabit): işlenmemiş aday · sahipsiz aday (yalnız atama yetkisi olana) · bugünkü ve geciken aktiviteler ·
 *   geciken sekans adımları · duraksayan fırsatlar · SLA riski taşıyan destek talepleri · onay bekleyenler.
 * - SAYAÇ = aynı süzgeçli listenin görünür kayıt toplamı: sayı ve liste AYNI sorgu yolundan (ACL'li liste derleyicisi ya da
 *   mevcut servis sorgusu) gelir; görünmeyen kayıt sayılmaz.
 * - Kapsam varsayılan "bana ait"; "ekibim" yalnız yöneticilere (bkz. `todayTeamAvailable`) ve departman alt ağacının etkin
 *   kullanıcılarına göre süzer. Kayıt erişimi (ACL) her iki kapsamda da değişmez.
 * - Uçlar yan etkisizdir (GET); hızlı eylemler kayıt sayfasının mevcut uçlarını kullanır (yeni yazma ucu yoktur).
 */
import { z } from 'zod';
import { canAssignOwner } from './assignment.js';
const uuid = z.guid();
const isoDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
export const TODAY_BUCKETS = [
    'leads_new',
    'leads_unassigned',
    'activities',
    'sequence_steps',
    'stalled_opportunities',
    'tickets_sla',
    // v3.1 S10: yanıt bekleyen konuşmalar (sorumlusu çağıran ya da ekip kuyruğunda sahipsiz)
    'conversations_unanswered',
    // EK1: ajanın devir istediği konuşmalar ve onay bekleyen ajan önerileri
    'agent_handoffs',
    'agent_suggestions',
    'approvals',
];
export const TODAY_BUCKET_LABELS = {
    leads_new: 'İşlenmemiş adaylar',
    leads_unassigned: 'Sahipsiz adaylar',
    activities: 'Bugünkü ve geciken aktiviteler',
    sequence_steps: 'Geciken sekans adımları',
    stalled_opportunities: 'Duraksayan fırsatlar',
    tickets_sla: 'Süre hedefi riskli destek talepleri',
    conversations_unanswered: 'Yanıt bekleyen konuşmalar',
    agent_handoffs: 'Devir bekleyen konuşmalar',
    agent_suggestions: 'Onayınızı bekleyen öneriler',
    approvals: 'Onay bekleyenler',
};
export const TODAY_SCOPES = ['mine', 'team'];
export const TODAY_SCOPE_LABELS = {
    mine: 'Bana ait',
    team: 'Ekibim',
};
export const TODAY_LIMITS = {
    /** Destek talebinde "yaklaşan" süre hedefi: hedef anına bu kadar saat (ya da daha az) kalmış */
    slaWarnHours: 4,
    /** Liste sayfası üst sınırı */
    pageMax: 100,
    pageDefault: 25,
    /** Sayaç bu değerin altında kesin; üstünde "en az" (ADR-0006) */
    countCap: 10_000,
};
/**
 * Sahipsiz aday kovası: ortak atama sözleşmesinin yetki kuralıyla (C0, `canAssignOwner`) — sorumlu atayabilen kullanıcıya
 * görünür; kova içeriği kullanıcının YAZABİLDİĞİ (yani sorumlusunu atayabildiği) sahipsiz adaylardır.
 */
export function todayCanAssign(perms) {
    return canAssignOwner(perms.capabilities);
}
/** "Ekibim" kapsamı: sistem yöneticisi ya da kullanıcı/departman yönetme yetkisi olan kullanıcılar */
export function todayTeamAvailable(perms) {
    return (perms.role === 'system_admin' ||
        perms.capabilities.includes('users.manage') ||
        perms.capabilities.includes('departments.manage'));
}
export const todaySummaryQuerySchema = z
    .object({ scope: z.enum(TODAY_SCOPES).default('mine') })
    .strict();
export const todayListQuerySchema = z
    .object({
    scope: z.enum(TODAY_SCOPES).default('mine'),
    cursor: z.string().min(1).max(600).optional(),
    limit: z.coerce
        .number()
        .int()
        .min(1)
        .max(TODAY_LIMITS.pageMax)
        .default(TODAY_LIMITS.pageDefault),
})
    .strict();
export const todayBucketSummarySchema = z.object({
    key: z.enum(TODAY_BUCKETS),
    /** Aynı süzgeçli listenin görünür kayıt sayısı */
    count: z.number().int(),
    /** `false`: sayı üst sınırda kesildi ("en az") */
    exact: z.boolean(),
});
export const todaySummarySchema = z.object({
    /** Kiracı saat diliminde bugün */
    today: isoDate,
    timeZone: z.string(),
    scope: z.enum(TODAY_SCOPES),
    teamAvailable: z.boolean(),
    /** Çağıranın görebildiği kovalar (sabit sırada) */
    buckets: z.array(todayBucketSummarySchema),
});
export const todayItemSchema = z.object({
    /** Kova içinde tekil anahtar (kayıt, sekans çalışması ya da onay talebi kimliği) */
    key: z.string(),
    record: z.object({ type: z.string(), id: uuid, title: z.string() }),
    ownerName: z.string().nullable(),
    /** Durum / aşama etiketi (Türkçe) */
    status: z.string().nullable(),
    /** Bağlam satırı (ör. "Sekans: … · Adım: …", "Aşamada 12 gün", "Tutar: …") */
    detail: z.string().nullable(),
    /** Gün (YYYY-AA-GG; aktivite başlangıcı, sekans adımı bitişi) */
    dueOn: isoDate.nullable(),
    /** An (ISO; süre hedefi, aktivite saati, onay talebi) */
    dueAt: z.string().nullable(),
    overdue: z.boolean(),
    /** Aktivite kovasında: kayıt sayfasındaki "Tamamla" eylemi kullanılabilir mi */
    completable: z.boolean().default(false),
    /**
     * B2: aktivite kovasında asıl ilgili kayıt (müşteri/aday/fırsat) — YALNIZ çağıranın görebildiği bağlantı; görünmeyenin adı/kimliği
     * dönmez (`null`). Eski sürüm alanı göndermez.
     */
    related: z.object({ type: z.string(), id: uuid, title: z.string() }).nullable().default(null),
    /**
     * F4: işi getiren asistan (devir ve öneri kovaları) — profilin görünen adı (`null` → arayüz "Asistan"); arayüz adı "Yapay zekâ"
     * rozetiyle çizer. Eski sürüm alanı göndermez.
     */
    assistant: z.object({ name: z.string().nullable() }).nullable().default(null),
});
export const todayListSchema = z.object({
    bucket: z.enum(TODAY_BUCKETS),
    scope: z.enum(TODAY_SCOPES),
    data: z.array(todayItemSchema),
    nextCursor: z.string().nullable(),
    /** Sayaçla AYNI değer (aynı süzgeç, aynı ACL) */
    total: z.number().int(),
    exact: z.boolean(),
});
//# sourceMappingURL=today.js.map